import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const BASE = "https://cbackend.xraidigital.com/api";
const CAMPS_URL = `${BASE}/camps/`;
const TECHNICIANS_URL = `${BASE}/technician/technicians/`;
const ADVANCE_URL = `${BASE}/technician/advance-payments/`;

const BRAND = "#11a8a4";
const BRAND_LIGHT = "#e0f5f4";
const BRAND_DARK = "#0c7a77";

const PAYMENT_MODES = [
  { value: "CASH",  label: "Cash",  icon: "💵" },
  { value: "BANK",  label: "Bank",  icon: "🏦" },
  { value: "UPI",   label: "UPI",   icon: "📲" },
];
const MODE_COLORS = {
  CASH: { bg: "#eaf3de", color: "#3b6d11" },
  BANK: { bg: "#e6f1fb", color: "#185fa5" },
  UPI:  { bg: "#eeedfe", color: "#3c3489" },
};

const fmt = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${parseInt(day)} ${mo[parseInt(m)-1]} ${y}`;
};
const inr = (n) =>
  Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ── shared fetch helper ── */
async function apiFetch(url, opts = {}) {
  const token = localStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
    ...(token
      ? { Authorization: `Token ${token}` }
      : {}),
    ...(opts.headers || {})
  };

  const res = await fetch(url, {
    ...opts,
    headers
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }

  return res.json();
}

/* ══════════════════════════════════════════════
   CAMP CARD
══════════════════════════════════════════════ */
function CampCard({ camp, selected, onSelect }) {
  const label = [camp.location, camp.district, camp.state].filter(Boolean).join(", ");
  return (
    <div
      onClick={() => onSelect(camp)}
      style={{
        padding: "10px 12px", cursor: "pointer", borderRadius: 10,
        border: selected ? `1.5px solid ${BRAND}` : "0.5px solid #ddd",
        background: selected ? BRAND_LIGHT : "#fff", transition: "all .15s",
        position: "relative",
      }}
    >
      {camp.is_northstar && (
        <span style={{ position:"absolute", top:7, right:8, fontSize:10,
          background:"#faeeda", color:"#854f0b", borderRadius:20, padding:"2px 7px", fontWeight:600 }}>
          ★ Northstar
        </span>
      )}
      <div style={{ fontSize:13, fontWeight:600, color: selected ? BRAND_DARK : "#222",
        marginBottom:2, paddingRight: camp.is_northstar ? 60 : 0, whiteSpace:"nowrap",
        overflow:"hidden", textOverflow:"ellipsis" }}>
        {label}
      </div>
      <div style={{ fontSize:11, color:"#888" }}>
        {fmt(camp.start_date)} – {fmt(camp.end_date)}
      </div>
      <div style={{ fontSize:11, color:"#bbb", marginTop:1 }}>
        {camp.client_name} · #{camp.client}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   CAMP BANNER
══════════════════════════════════════════════ */
function CampBanner({ camp }) {
  if (!camp) return null;
  const status = camp.is_completed ? "Completed" : camp.ready_to_go ? "Ready" : "Active";
  return (
    <div style={{ background: BRAND_LIGHT, border:`0.5px solid ${BRAND}`, borderRadius:12,
      padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:12 }}>
      <div style={{ width:10, height:10, borderRadius:"50%", background:BRAND,
        marginTop:5, flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:14, fontWeight:600, color:BRAND_DARK,
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
          {[camp.location, camp.district, camp.state].filter(Boolean).join(", ")}
        </div>
        <div style={{ fontSize:12, color:"#0f9490", marginTop:3, lineHeight:1.5 }}>
          {fmt(camp.start_date)} – {fmt(camp.end_date)} &nbsp;·&nbsp;
          PIN {camp.pin_code} &nbsp;·&nbsp; Client: {camp.client_name}
          {camp.branch_name && <> &nbsp;·&nbsp; Branch: {camp.branch_name}</>}
        </div>
      </div>
      <span style={{ background:BRAND, color:"#fff", fontSize:11, padding:"3px 10px",
        borderRadius:20, fontWeight:500, flexShrink:0 }}>{status}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════
   NEW PAYMENT TAB
══════════════════════════════════════════════ */
function NewPaymentTab({ onCreated }) {
  const [camps, setCamps]       = useState([]);
  const [techs, setTechs]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [apiErr, setApiErr]     = useState("");

  /* form state */
  const [campSearch, setCampSearch] = useState("");
  const [techSearch, setTechSearch] = useState("");
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [techId, setTechId]     = useState("");
  const [amount, setAmount]     = useState("");
  const [payDate, setPayDate]   = useState(new Date().toISOString().split("T")[0]);
  const [mode, setMode]         = useState("CASH");
  const [reference, setReference] = useState("");
  const [remarks, setRemarks]   = useState("");
  const [fileName, setFileName] = useState("");
  const [fileObj, setFileObj]   = useState(null);
  const [errors, setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]       = useState("");

  const needsRef = mode === "BANK" || mode === "UPI";

  useEffect(() => {
    Promise.all([apiFetch(CAMPS_URL), apiFetch(TECHNICIANS_URL)])
      .then(([c, t]) => { setCamps(c); setTechs(t); })
      .catch(e => setApiErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filteredCamps = camps.filter(c => {
    const q = campSearch.toLowerCase();
    return !q || [c.location, c.district, c.state, c.client_name, c.client].some(
      v => v && v.toLowerCase().includes(q));
  });

  const filteredTechs = techs.filter(t => {
    const q = techSearch.toLowerCase();

    return (
        !q ||
        t.name?.toLowerCase().includes(q) ||
        t.phone?.toLowerCase().includes(q) ||
        String(t.id).includes(q)
    );
    });

  function validate() {
    const e = {};
    if (!selectedCamp) e.camp = "Select a camp";
    if (!techId) e.tech = "Select a technician";
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) e.amount = "Enter a valid amount";
    if (!payDate) e.payDate = "Select payment date";
    if (needsRef && !reference.trim()) e.reference = `Reference required for ${mode}`;
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setSubmitting(true);
    try {
      let body;
      if (fileObj) {
        body = new FormData();
        body.append("technician", techId);
        body.append("camp", selectedCamp.id);
        body.append("amount", parseFloat(amount).toFixed(2));
        body.append("payment_mode", mode);
        body.append("payment_date", payDate);
        body.append("reference", reference);
        body.append("remarks", remarks);
        body.append("attachment", fileObj);
        const token = localStorage.getItem("token");

        const res = await fetch(
        ADVANCE_URL,
        {
            method: "POST",
            headers: {
            Authorization: `Token ${token}`
            },
            body
        }
        );
        if (!res.ok) throw new Error(await res.text());
        const created = await res.json();
        onCreated(created);
      } else {
        const payload = {
          technician: parseInt(techId),
          camp: selectedCamp.id,
          amount: parseFloat(amount).toFixed(2),
          payment_mode: mode,
          payment_date: payDate,
          reference,
          remarks,
        };
        const created = await apiFetch(ADVANCE_URL, { method:"POST", body: JSON.stringify(payload) });
        onCreated(created);
      }
      /* reset */
      setTechId(""); setAmount(""); setReference(""); setRemarks("");
      setFileName(""); setFileObj(null); setMode("CASH"); setSelectedCamp(null);
      setToast("Payment recorded successfully");
      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      setToast(""); setErrors({ submit: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  const inp = (err) => ({
    width:"100%", padding:"9px 12px", fontSize:14, fontFamily:"inherit",
    border:`0.5px solid ${err ? "#e24b4a" : "#ddd"}`, borderRadius:8,
    background:"#fff", color:"#222", boxSizing:"border-box", transition:"border .15s",
  });
  const lbl = { fontSize:13, fontWeight:600, color:"#555", display:"block", marginBottom:5 };
  const errTxt = { fontSize:11, color:"#e24b4a", marginTop:4 };

  if (loading) return (
    <div style={{ textAlign:"center", padding:"3rem", color:"#aaa" }}>
      <div style={{ fontSize:28, marginBottom:8 }}>⏳</div>Loading data...
    </div>
  );
  if (apiErr) return (
    <div style={{ background:"#fcebeb", border:"0.5px solid #f09595", borderRadius:10,
      padding:"14px 16px", color:"#a32d2d", fontSize:13 }}>
      Failed to load: {apiErr}
    </div>
  );

  return (
    <div>
      {toast && (
        <div style={{ background:BRAND, color:"#fff", borderRadius:8, padding:"10px 16px",
          fontSize:13, fontWeight:500, marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
          ✓ {toast}
        </div>
      )}
      {errors.submit && (
        <div style={{ background:"#fcebeb", border:"0.5px solid #f09595", borderRadius:8,
          padding:"10px 14px", color:"#a32d2d", fontSize:13, marginBottom:14 }}>
          ✗ {errors.submit}
        </div>
      )}

      {/* CAMP SELECTOR */}
      <div style={{ background:"#fff", borderRadius:12, border:"0.5px solid #e5e5e5",
        padding:"16px 18px", marginBottom:14 }}>
        <div style={{ fontSize:14, fontWeight:600, color:"#222", marginBottom:10 }}>
          Select camp <span style={{ color:BRAND }}>*</span>
        </div>
        <input
          placeholder="Search by location, district, client…"
          value={campSearch}
          onChange={e => setCampSearch(e.target.value)}
          style={{ ...inp(errors.camp), marginBottom:10 }}
        />
        {errors.camp && <div style={{ ...errTxt, marginTop:-6, marginBottom:8 }}>{errors.camp}</div>}
        <div style={{ maxHeight:220, overflowY:"auto", display:"grid",
          gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {filteredCamps.length === 0
            ? <div style={{ gridColumn:"span 2", textAlign:"center",
                padding:"1rem", color:"#aaa", fontSize:13 }}>No camps found</div>
            : filteredCamps.map(c => (
              <CampCard key={c.id} camp={c}
                selected={selectedCamp?.id === c.id}
                onSelect={(camp) => { setSelectedCamp(camp); setErrors(p => ({...p, camp:null})); }} />
            ))
          }
        </div>
      </div>

      {selectedCamp && <CampBanner camp={selectedCamp} />}

      {/* PAYMENT FORM */}
      <div style={{ background:"#fff", borderRadius:12, border:"0.5px solid #e5e5e5",
        padding:"16px 18px" }}>
        <div style={{ fontSize:14, fontWeight:600, color:"#222", marginBottom:14 }}>
          Payment details
        </div>

        {/* Technician */}
        <div style={{ marginBottom:14 }}>
            <label style={lbl}>
                Technician <span style={{ color:BRAND }}>*</span>
            </label>

            <input
                type="text"
                placeholder="Search technician by name, phone, ID..."
                value={techSearch}
                onChange={(e) => setTechSearch(e.target.value)}
                style={{
                ...inp(errors.tech),
                marginBottom: 8
                }}
            />

            <div
                style={{
                maxHeight: 220,
                overflowY: "auto",
                border: "0.5px solid #ddd",
                borderRadius: 8,
                background: "#fff"
                }}
            >
                {filteredTechs.length === 0 ? (
                <div
                    style={{
                    padding: 12,
                    textAlign: "center",
                    color: "#999",
                    fontSize: 13
                    }}
                >
                    No technician found
                </div>
                ) : (
                filteredTechs.map((tech) => (
                    <div
                    key={tech.id}
                    onClick={() => {
                        setTechId(tech.id);
                        setTechSearch(tech.name);
                        setErrors((p) => ({
                        ...p,
                        tech: null
                        }));
                    }}
                    style={{
                        padding: "10px 12px",
                        cursor: "pointer",
                        borderBottom: "0.5px solid #f0f0f0",
                        background:
                        String(techId) === String(tech.id)
                            ? BRAND_LIGHT
                            : "#fff"
                    }}
                    >
                    <div
                        style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#222"
                        }}
                    >
                        {tech.name}
                    </div>

                    {tech.phone && (
                        <div
                        style={{
                            fontSize: 11,
                            color: "#888",
                            marginTop: 2
                        }}
                        >
                        {tech.phone}
                        </div>
                    )}
                    </div>
                ))
                )}
            </div>

            {errors.tech && (
                <div style={errTxt}>
                {errors.tech}
                </div>
            )}
            </div>

        {/* Amount + Date */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <div>
            <label style={lbl}>Amount (₹) <span style={{ color:BRAND }}>*</span></label>
            <input type="number" min="0" step="0.01" placeholder="0.00"
              value={amount}
              onChange={e => { setAmount(e.target.value); setErrors(p => ({...p, amount:null})); }}
              style={inp(errors.amount)} />
            {errors.amount && <div style={errTxt}>{errors.amount}</div>}
          </div>
          <div>
            <label style={lbl}>Payment date <span style={{ color:BRAND }}>*</span></label>
            <input type="date" value={payDate}
              onChange={e => { setPayDate(e.target.value); setErrors(p => ({...p, payDate:null})); }}
              style={inp(errors.payDate)} />
            {errors.payDate && <div style={errTxt}>{errors.payDate}</div>}
          </div>
        </div>

        {/* Payment mode */}
        <div style={{ marginBottom:14 }}>
          <label style={lbl}>Payment mode <span style={{ color:BRAND }}>*</span></label>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
            {PAYMENT_MODES.map(m => (
              <div key={m.value} onClick={() => { setMode(m.value); setErrors(p=>({...p,reference:null})); }}
                style={{ padding:"10px 0", textAlign:"center", borderRadius:8, cursor:"pointer",
                  border: mode===m.value ? `1.5px solid ${BRAND}` : "0.5px solid #ddd",
                  background: mode===m.value ? BRAND_LIGHT : "#fafafa",
                  color: mode===m.value ? BRAND_DARK : "#666",
                  fontSize:13, fontWeight:500, transition:"all .15s" }}>
                <div style={{ fontSize:18, marginBottom:2 }}>{m.icon}</div>
                {m.label}
              </div>
            ))}
          </div>
        </div>

        {/* Reference (conditional) */}
        {needsRef && (
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Reference number <span style={{ color:BRAND }}>*</span></label>
            <input type="text" placeholder="Transaction ID / UTR number"
              value={reference}
              onChange={e => { setReference(e.target.value); setErrors(p => ({...p, reference:null})); }}
              style={inp(errors.reference)} />
            {errors.reference && <div style={errTxt}>{errors.reference}</div>}
          </div>
        )}

        {/* Remarks */}
        <div style={{ marginBottom:14 }}>
          <label style={lbl}>Remarks</label>
          <textarea placeholder="Optional notes…" value={remarks}
            onChange={e => setRemarks(e.target.value)} rows={3}
            style={{ ...inp(false), resize:"vertical", fontFamily:"inherit" }} />
        </div>

        {/* Attachment */}
        <div style={{ marginBottom:18 }}>
          <label style={lbl}>Attachment</label>
          <label style={{ display:"block", border:"0.5px dashed #ccc", borderRadius:8,
            padding:"14px", textAlign:"center", cursor:"pointer", fontSize:13, color:"#888" }}>
            <div style={{ fontSize:20, marginBottom:4 }}>📎</div>
            {fileName || "Attach receipt or screenshot"}
            <input type="file" accept="image/*,.pdf" style={{ display:"none" }}
              onChange={e => {
                const f = e.target.files[0];
                if (f) { setFileName(f.name); setFileObj(f); }
              }} />
          </label>
        </div>

        <button onClick={handleSubmit} disabled={submitting}
          style={{ width:"100%", padding:11, background: submitting ? "#7dd4d1" : BRAND,
            color:"#fff", border:"none", borderRadius:8, fontSize:14, fontWeight:600,
            cursor: submitting ? "not-allowed" : "pointer" }}>
          {submitting ? "Saving…" : "✓ Record payment"}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   HISTORY TAB
══════════════════════════════════════════════ */
function HistoryTab() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");
  const [filter, setFilter]     = useState("ALL");

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(ADVANCE_URL)
      .then(setPayments)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "ALL" ? payments
    : payments.filter(p => p.payment_mode === filter);

  if (loading) return (
    <div style={{ textAlign:"center", padding:"3rem", color:"#aaa" }}>
      <div style={{ fontSize:28, marginBottom:8 }}>⏳</div>Loading payments…
    </div>
  );
  if (err) return (
    <div style={{ background:"#fcebeb", borderRadius:10, padding:"14px",
      color:"#a32d2d", fontSize:13 }}>
      Error: {err} &nbsp;
      <button onClick={load} style={{ color:BRAND, background:"none", border:"none",
        cursor:"pointer", fontWeight:600 }}>Retry</button>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
        {["ALL","CASH","BANK","UPI"].map(f => (
          <div key={f} onClick={() => setFilter(f)}
            style={{ padding:"5px 14px", borderRadius:20, fontSize:12, fontWeight:500,
              cursor:"pointer", transition:"all .15s",
              border:`0.5px solid ${filter===f ? BRAND : "#ddd"}`,
              background: filter===f ? BRAND_LIGHT : "#fafafa",
              color: filter===f ? BRAND : "#666" }}>
            {f === "ALL" ? "All payments" : f}
          </div>
        ))}
        <div style={{ marginLeft:"auto" }}>
          <button onClick={load}
            style={{ fontSize:12, color:BRAND, background:"none", border:`0.5px solid ${BRAND}`,
              borderRadius:20, padding:"5px 14px", cursor:"pointer", fontWeight:500 }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      <div style={{ background:"#fff", borderRadius:12, border:"0.5px solid #e5e5e5",
        padding:"4px 18px" }}>
        {filtered.length === 0
          ? <div style={{ textAlign:"center", padding:"2rem", color:"#aaa", fontSize:14 }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🧾</div>
              No payments found
            </div>
          : filtered.map((p, i) => (
            <div key={p.id} style={{ display:"flex", alignItems:"center", gap:12,
              padding:"14px 0", borderBottom: i < filtered.length-1 ? "0.5px solid #f0f0f0" : "none" }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:BRAND_LIGHT,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:16, flexShrink:0 }}>👤</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:"#222" }}>
                  {p.technician_name || `Tech #${p.technician}`}
                </div>
                <div style={{ fontSize:12, color:"#aaa", marginTop:2 }}>
                  Camp #{p.camp} &nbsp;·&nbsp; {fmt(p.payment_date)}
                </div>
                {p.reference && (
                <div style={{ fontSize:11, color:"#bbb", marginTop:1 }}>
                    Ref: {p.reference}
                </div>
                )}

                {p.remarks && (
                <div style={{ fontSize:11, color:"#888", marginTop:2 }}>
                    {p.remarks}
                </div>
                )}

                {p.attachment && (
                <div style={{ marginTop:6 }}>
                    <a
                    href={p.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: BRAND,
                        fontSize: 12,
                        fontWeight: 600,
                        textDecoration: "none"
                    }}
                    >
                    📎 View Attachment
                    </a>
                </div>
                )}
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <div style={{ fontSize:15, fontWeight:600, color:"#222" }}>
                  ₹{inr(p.amount)}
                </div>
                <span style={{ display:"inline-block", fontSize:11, padding:"2px 9px",
                  borderRadius:20, marginTop:4, fontWeight:600,
                  background: MODE_COLORS[p.payment_mode]?.bg || "#eee",
                  color: MODE_COLORS[p.payment_mode]?.color || "#555" }}>
                  {p.payment_mode}
                </span>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   SUMMARY TAB
══════════════════════════════════════════════ */
function SummaryTab() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");

  useEffect(() => {
    apiFetch(ADVANCE_URL)
      .then(setPayments)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ textAlign:"center", padding:"3rem", color:"#aaa" }}>
      <div style={{ fontSize:28, marginBottom:8 }}>⏳</div>Loading…
    </div>
  );
  if (err) return (
    <div style={{ background:"#fcebeb", borderRadius:10, padding:"14px",
      color:"#a32d2d", fontSize:13 }}>Error: {err}</div>
  );

  const total = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const avg   = payments.length ? total / payments.length : 0;
  const byMode = { CASH:0, BANK:0, UPI:0 };
  payments.forEach(p => { byMode[p.payment_mode] = (byMode[p.payment_mode]||0) + parseFloat(p.amount||0); });

  const byTech = {};
  payments.forEach(p => {
    const n = p.technician_name || `Tech #${p.technician}`;
    if (!byTech[n]) byTech[n] = { total:0, count:0 };
    byTech[n].total += parseFloat(p.amount||0);
    byTech[n].count++;
  });
  const topTechs = Object.entries(byTech).sort((a,b) => b[1].total - a[1].total).slice(0,8);

  const statCard = (val, label) => (
    <div style={{ background:"#f7fffe", border:`0.5px solid ${BRAND}33`, borderRadius:10,
      padding:"14px 16px", textAlign:"center" }}>
      <div style={{ fontSize:20, fontWeight:700, color:BRAND }}>{val}</div>
      <div style={{ fontSize:12, color:"#888", marginTop:4 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:14 }}>
        {statCard(`₹${inr(total)}`, "Total paid")}
        {statCard(payments.length, "Payments")}
        {statCard(`₹${inr(avg)}`, "Avg. amount")}
      </div>

      <div style={{ background:"#fff", borderRadius:12, border:"0.5px solid #e5e5e5",
        padding:"16px 18px", marginBottom:14 }}>
        <div style={{ fontSize:14, fontWeight:600, color:"#222", marginBottom:14 }}>
          By payment mode
        </div>
        {PAYMENT_MODES.map(m => {
          const val = byMode[m.value] || 0;
          const pct = total > 0 ? (val / total) * 100 : 0;
          return (
            <div key={m.value} style={{ marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                fontSize:13, marginBottom:5 }}>
                <span style={{ color:"#555", fontWeight:500 }}>{m.icon} {m.label}</span>
                <span style={{ color:"#222", fontWeight:600 }}>₹{inr(val)}</span>
              </div>
              <div style={{ height:6, borderRadius:6, background:"#f0f0f0", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${pct}%`, background:BRAND,
                  borderRadius:6, transition:"width .4s" }} />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background:"#fff", borderRadius:12, border:"0.5px solid #e5e5e5",
        padding:"16px 18px" }}>
        <div style={{ fontSize:14, fontWeight:600, color:"#222", marginBottom:12 }}>
          Top technicians
        </div>
        {topTechs.length === 0
          ? <div style={{ textAlign:"center", padding:"1.5rem", color:"#aaa", fontSize:14 }}>
              No payments yet
            </div>
          : topTechs.map(([name, d], i, arr) => (
            <div key={name} style={{ display:"flex", alignItems:"center", gap:12,
              padding:"12px 0", borderBottom: i<arr.length-1 ? "0.5px solid #f0f0f0" : "none" }}>
              <div style={{ width:32, height:32, borderRadius:"50%", background:BRAND_LIGHT,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:13, fontWeight:700, color:BRAND, flexShrink:0 }}>
                {i+1}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"#222",
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{name}</div>
                <div style={{ fontSize:11, color:"#aaa" }}>
                  {d.count} payment{d.count>1?"s":""}
                </div>
              </div>
              <div style={{ fontSize:14, fontWeight:600, color:"#222" }}>
                ₹{inr(d.total)}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ROOT
══════════════════════════════════════════════ */
export default function AdvancePayment() {
  const [tab, setTab] = useState("new");
  const [historyKey, setHistoryKey] = useState(0);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("username");

    navigate("/login");
  };



  function handleCreated() {
    setHistoryKey(k => k+1);
  }

  const TABS = [
    { key:"new",     label:"New Payment" },
    { key:"history", label:"History" },
    { key:"summary", label:"Summary" },
  ];

  return (
    <div style={{ maxWidth:720, margin:"0 auto", padding:"24px 16px",
      fontFamily:"Inter, system-ui, sans-serif", background:"#f5f7f7", minHeight:"100vh" }}>
      <style>{`
        *{box-sizing:border-box}
        input:focus,select:focus,textarea:focus{
          border-color:${BRAND}!important;
          box-shadow:0 0 0 3px ${BRAND}22;outline:none;
        }
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-thumb{background:#ccc;border-radius:4px}
      `}</style>

      {/* HEADER */}
      <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20
        }}
        >
        <div
            style={{
            display: "flex",
            alignItems: "center",
            gap: 12
            }}
        >
            <div
            style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: BRAND,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                flexShrink: 0
            }}
            >
            💼
            </div>

            <div>
            <div
                style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#1a1a1a"
                }}
            >
                Advance Payments
            </div>

            <div
                style={{
                fontSize: 13,
                color: "#888"
                }}
            >
                Issue and track technician payments by camp
            </div>
            </div>
        </div>

        <button
            onClick={handleLogout}
            style={{
            background: "#dc3545",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "8px 16px",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer"
            }}
        >
            🚪 Logout
        </button>
        </div>

      {/* TABS */}
      <div style={{ display:"flex", gap:4, background:"#ebebeb", borderRadius:10,
        padding:4, marginBottom:20 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ flex:1, padding:"8px 0", borderRadius:8, border:"none",
              fontSize:13, fontWeight:600, cursor:"pointer", transition:"all .15s",
              background: tab===t.key ? "#fff" : "transparent",
              color: tab===t.key ? BRAND : "#888",
              boxShadow: tab===t.key ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "new"     && <NewPaymentTab onCreated={handleCreated} />}
      {tab === "history" && <HistoryTab key={historyKey} />}
      {tab === "summary" && <SummaryTab />}
    </div>
  );
}